import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ApiError, type AppDetails, type SdkKeySummary } from '../api/client.ts';
import { Button, ConfirmDialog, CopyId, Modal } from '@bb/ui';
import { Can } from '../features/access/Can.tsx';
import { usePermissions } from '../features/access/usePermissions.ts';

const PLATFORM_LABEL = { android: 'Android', ios: 'iOS', web: 'Web' } as const;
const KEY_STATUS = { active: 'Active', retiring: 'Retiring', retired: 'Retired', expired: 'Expired', revoked: 'Revoked' } as const;
const MARKER = { unsupported: 'Unsupported', update_available: 'Update available', current: 'Current' } as const;
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const shortTime = (iso: string | null) => (iso ? `${iso.slice(0, 16).replace('T', ' ')} UTC` : 'Never');

/** Settings of one app instance: identifiers, SDK keys, uninstall tracking, SDK versions in the field (BB-13). */
export function AppDetailPage() {
  const { environmentId, appId } = useParams({ from: '/env/$environmentId/settings/apps/$appId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/apps/$appId' });
  const details = useQuery({ queryKey: ['app', environmentId, appId], queryFn: () => api.appDetails(environmentId, appId) });

  if (details.isPending) return <p aria-busy="true">Loading app…</p>;
  if (details.isError) return <p role="alert">{errorText(details.error)}</p>;
  const { app } = details.data;

  return (
    <section>
      <h1>
        {app.name} <span className="tag tag--neutral">{PLATFORM_LABEL[app.platform]}</span>
      </h1>
      <dl className="ids">
        <dt>Identifier</dt>
        <dd>
          <code>{app.identifier}</code>
        </dd>
        <dt>App API ID</dt>
        <dd>
          <CopyId value={app.apiId} label="app API ID" />
        </dd>
        <dt>SDK endpoint</dt>
        <dd>
          <CopyId value={details.data.sdkEndpoint} label="SDK endpoint" />
        </dd>
      </dl>
      <AppSettingsForm key={`${app.uninstallTrackingEnabled}-${app.minSupportedSdkVersion}`} data={details.data} />
      <SdkKeys keys={details.data.sdkKeys} />
      <SdkVersions data={details.data} />
    </section>
  );
}

function useAppParams() {
  const params = useParams({ from: '/env/$environmentId/settings/apps/$appId' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/apps/$appId' });
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['app', params.environmentId, params.appId] });
  return { ...params, api, refresh };
}

function AppSettingsForm({ data }: { data: AppDetails }) {
  const { environmentId, appId, api, refresh } = useAppParams();
  const canEdit = usePermissions('environment').has('apps.manage');
  const [uninstall, setUninstall] = useState(data.app.uninstallTrackingEnabled);
  const [minVersion, setMinVersion] = useState(data.app.minSupportedSdkVersion ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.updateApp(environmentId, appId, { uninstallTrackingEnabled: uninstall, minSupportedSdkVersion: minVersion.trim() || null }),
    onSuccess: refresh,
  });
  const submit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  return (
    <form className="stack-form" aria-label="App settings" onSubmit={submit}>
      <h2>Settings</h2>
      <label>
        <input type="checkbox" checked={uninstall} disabled={!canEdit} onChange={(e) => setUninstall(e.target.checked)} /> Uninstall tracking
      </label>
      <label>
        Minimum supported SDK version
        <input value={minVersion} disabled={!canEdit} placeholder="1.2.0" onChange={(e) => setMinVersion(e.target.value)} />
      </label>
      {canEdit && (
        <button type="submit" disabled={save.isPending}>
          Save settings
        </button>
      )}
      {save.isSuccess && <span role="status">Saved</span>}
      {save.isError && <span role="alert">{errorText(save.error)}</span>}
    </form>
  );
}

function SdkKeys({ keys }: { keys: SdkKeySummary[] }) {
  const { environmentId, appId, api, refresh } = useAppParams();
  const [issued, setIssued] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<{ action: 'rotate' | 'revoke'; key: SdkKeySummary } | null>(null);
  const create = useMutation({ mutationFn: () => api.createSdkKey(environmentId, appId), onSuccess: (r) => (setIssued(r.key), refresh()) });
  const act = useMutation({
    mutationFn: async ({ action, key }: { action: 'rotate' | 'revoke'; key: SdkKeySummary }): Promise<string | null> =>
      action === 'rotate' ? (await api.rotateSdkKey(environmentId, appId, key.id)).key : (await api.revokeSdkKey(environmentId, appId, key.id), null),
    onSuccess: (newKey) => {
      if (newKey) setIssued(newKey);
      setConfirm(null);
      void refresh();
    },
  });

  return (
    <section aria-label="SDK keys">
      <h2>SDK keys</h2>
      <p className="hint">
        Apps send the key as <code>X-BB-App-Key</code>. A rotated key keeps working for 14 days so released builds can update.
      </p>
      {keys.length === 0 ? (
        <p className="empty-state">No SDK keys yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Key</th>
              <th scope="col">Status</th>
              <th scope="col">Last used</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map((key) => (
              <tr key={key.id}>
                <td>
                  <code>{key.keyPrefix}…</code>
                </td>
                <td>
                  {KEY_STATUS[key.status]}
                  {key.status === 'retiring' && key.retiringUntil ? ` until ${shortTime(key.retiringUntil)}` : ''}
                </td>
                <td>{shortTime(key.lastUsedAt)}</td>
                <td className="actions">
                  <Can permission="apps.manage">
                    {key.status === 'active' && (
                      <button type="button" onClick={() => setConfirm({ action: 'rotate', key })} aria-label={`Rotate ${key.keyPrefix}`}>
                        Rotate
                      </button>
                    )}
                    {(key.status === 'active' || key.status === 'retiring') && (
                      <button type="button" onClick={() => setConfirm({ action: 'revoke', key })} aria-label={`Revoke ${key.keyPrefix}`}>
                        Revoke
                      </button>
                    )}
                  </Can>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm ? `${confirm.action === 'rotate' ? 'Rotate' : 'Revoke'} ${confirm.key.keyPrefix}…?` : ''}
        confirmLabel={confirm?.action === 'rotate' ? 'Rotate key' : 'Revoke key'}
        tone={confirm?.action === 'rotate' ? 'primary' : 'danger'}
        busy={act.isPending}
        onConfirm={() => confirm && act.mutate(confirm)}
      >
        {confirm?.action === 'rotate' ? 'A new key is issued; the current one keeps working for 14 days.' : 'Apps using this key stop working immediately.'}
      </ConfirmDialog>
      {act.isError && <p role="alert">{errorText(act.error)}</p>}
      <Can permission="apps.manage">
        <button type="button" onClick={() => create.mutate()} disabled={create.isPending}>
          Create SDK key
        </button>
        {create.isError && <span role="alert">{errorText(create.error)}</span>}
      </Can>
      <Modal
        open={issued !== null}
        onOpenChange={(open) => !open && setIssued(null)}
        title="New SDK key"
        description="Copy the key into the app configuration now: it is shown only once."
        footer={<Button onClick={() => setIssued(null)}>Done</Button>}
      >
        {issued && <CopyId value={issued} label="new SDK key" />}
      </Modal>
    </section>
  );
}

function SdkVersions({ data }: { data: AppDetails }) {
  return (
    <section aria-label="SDK versions">
      <h2>SDK versions in use</h2>
      <p className="hint">
        Latest SDK: {data.latestSdkVersion ?? 'not configured'} · minimum supported: {data.app.minSupportedSdkVersion ?? 'any'}
      </p>
      {data.sdkVersions.length === 0 ? (
        <p className="empty-state">No SDK traffic recorded yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">SDK</th>
              <th scope="col">App version</th>
              <th scope="col">Last seen</th>
              <th scope="col">Installations</th>
              <th scope="col">State</th>
            </tr>
          </thead>
          <tbody>
            {data.sdkVersions.map((v) => (
              <tr key={`${v.sdkVersion}-${v.appVersion}`}>
                <td>{v.sdkVersion}</td>
                <td>{v.appVersion}</td>
                <td>{shortTime(v.lastSeenAt)}</td>
                <td>{v.installationsEstimate ?? '—'}</td>
                <td>
                  <span className={`marker marker--${v.marker}`}>{MARKER[v.marker]}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
